/**
 * The "this browser session met the over-limit wall signed out" mark (kitecorp/transferify-ui#3196), React-free.
 *
 * ── Why a mark exists at all ─────────────────────────────────────────────────────────────────────
 * The signed-out wall is counted anonymously, with no account to join on, and everything a sender does after
 * signing in from it is reported signed in. Nothing on the api can join the two halves, so the browser says it
 * on every signed-in over-limit step: `arrivedSignedOut`, which the api stores beside the row. Two facts arrive
 * at different times: the wall is met before the session exists, and the steps that carry the flag happen
 * after it, possibly after a reload the sign-in caused.
 *
 * ── Why sessionStorage, and what the mark means ───────────────────────────────────────────────────
 * `sessionStorage` because the mark must survive a reload and a navigation within this tab and must NOT survive
 * the tab: a sender who comes back tomorrow in a new tab and meets the priced wall signed in did not arrive
 * through this session's signed-out wall, and a `localStorage` mark would say they did. The same reasoning
 * `analytics/signUpMark` gives for its own marks.
 *
 * It is set when the signed-out wall is REACHED (nothing renders for it since kitecorp/transferify-ui#3435), not
 * when the e-mail code step opens from it: a sender who reaches it and signs in through the nav bar instead still
 * arrived through it. It is never cleared within the tab,
 * on purpose: the funnel question counts accounts once per day and wall, so a sender who met the wall, signed
 * in and then sent again in the same tab is still one account that arrived signed out, and a mark that cleared
 * itself would turn their later checkout into a direct arrival. The tab closing is what ends it.
 *
 * The mark carries no identifier, only that the wall was met. Every read and write is guarded: a restricted or
 * full store, a private window or a server render answers "not arrived" and never throws into the composer.
 */

/** Namespaced so it cannot collide with the consent, attribution and sign-up keys this app already stores. */
export const ARRIVED_SIGNED_OUT_KEY = "transferify.overLimit.arrivedSignedOut";

/**
 * The stored value. Only this exact string counts as the mark, so a leftover or tampered value under the same
 * key cannot invent an arrival.
 */
const ARRIVED_VALUE = "1";

/**
 * Record that this tab's session met the over-limit wall signed out.
 *
 * @param storage the tab's session store, or null outside a browser; `core/browserStorage.browserSessionStorage`
 *                answers it, and the parameter is what keeps this module testable without a window
 */
export function rememberSignedOutWall(storage: Storage | null): void {
	try {
		storage?.setItem(ARRIVED_SIGNED_OUT_KEY, ARRIVED_VALUE);
	} catch {
		// no-state: a full or restricted store loses the mark and nothing else. This module touches no surface
		// state at all: the over-limit rung is the rule module's answer, and a lost mark only
		// makes a later signed-in step read as a direct arrival, which costs a measurement, never a way forward.
	}
}

/** Whether this tab's session met the over-limit wall signed out. False wherever the store cannot answer. */
export function arrivedSignedOut(storage: Storage | null): boolean {
	try {
		return storage?.getItem(ARRIVED_SIGNED_OUT_KEY) === ARRIVED_VALUE;
	} catch {
		// no-state: a store that cannot be read answers "not arrived", the same answer a browser that predates
		// the mark gives. No surface state depends on it; the over-limit rung is decided elsewhere.
		return false;
	}
}
